/**
 * ConfettiAnimation Component
 *
 * A reusable confetti animation that appears from the bottom of the screen.
 * Supports two levels: 'small' for updates and 'large' for achievements.
 *
 * @component
 * @param {boolean} trigger - When true, triggers the animation
 * @param {string} level - 'small' or 'large' (default: 'small')
 * @param {function} onComplete - Callback when animation completes naturally
 */

import React, { useEffect, useRef } from 'react';

function ConfettiAnimation({ trigger = false, level = 'small', onComplete }) {
  const containerRef = useRef(null);
  const animationIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!trigger) {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      return;
    }

    const container = containerRef.current;
    if (!container) {
      console.warn('[ConfettiAnimation] Container ref not available');
      return;
    }

    if (window.Configs?.devEnvironment) {
      console.log('[ConfettiAnimation] Triggering confetti animation, level:', level);
    }

    const particleCount = level === 'large' ? 150 : 80;
    const duration = level === 'large' ? 3000 : 2000;
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];

    // Clear any existing particles
    container.innerHTML = '';
    const particles = [];

    // Create particles with initial properties
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');

      const size = level === 'large'
        ? Math.random() * 12 + 8  // 8-20px for large
        : Math.random() * 8 + 4;  // 4-12px for small
      const left = Math.random() * 100; // 0-100% from left
      const color = colors[Math.floor(Math.random() * Math.max(colors.length, 1))];
      const endRotation = (Math.random() * 720 + 360) * (Math.random() > 0.5 ? 1 : -1);
      const endX = (Math.random() - 0.5) * (level === 'large' ? 400 : 250);
      const delay = Math.random() * 500; // 0-500ms delay
      const durationVariation = duration + (Math.random() * 1000 - 500);

      particle.style.cssText = `
        position: fixed;
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        left: ${left}%;
        bottom: -20px;
        border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
        opacity: 1;
        pointer-events: none;
        z-index: 9999;
        transform: translateY(0) translateX(0) rotate(0deg);
      `;

      container.appendChild(particle);

      particles.push({
        element: particle,
        startX: 0,
        endX: endX,
        startRotation: 0,
        endRotation: endRotation,
        delay: delay,
        duration: durationVariation,
        startTime: null
      });
    }

    let completedNaturally = false;
    const finish = () => {
      if (completedNaturally) return;
      completedNaturally = true;
      onCompleteRef.current?.();
    };

    // Animate using requestAnimationFrame
    startTimeRef.current = performance.now();

    function animate(currentTime) {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;

      particles.forEach((particle) => {
        const particleElapsed = elapsed - particle.delay;

        if (particleElapsed < 0) {
          return;
        }

        if (particleElapsed >= particle.duration) {
          particle.element.style.opacity = '0';
          particle.element.style.transform = `translateY(-120vh) translateX(${particle.endX}px) rotate(${particle.endRotation}deg)`;
          return;
        }

        const progress = particleElapsed / particle.duration;
        const eased = 1 - Math.pow(1 - progress, 3);
        const currentY = -120 * eased;
        const currentX = particle.endX * eased;
        const currentRotation = particle.endRotation * eased;
        const currentOpacity = 1 - eased;

        particle.element.style.transform = `translateY(${currentY}vh) translateX(${currentX}px) rotate(${currentRotation}deg)`;
        particle.element.style.opacity = String(Math.max(0, currentOpacity));
      });

      const allComplete = particles.every((p) => {
        const particleElapsed = elapsed - p.delay;
        return particleElapsed >= p.duration;
      });

      if (allComplete) {
        container.innerHTML = '';
        finish();
      } else {
        animationIdRef.current = requestAnimationFrame(animate);
      }
    }

    animationIdRef.current = requestAnimationFrame(animate);

    // Do NOT call onComplete on unmount — Strict Mode remounts and status-driven
    // re-renders were navigating away before confetti was visible. Parents that need
    // navigate-after-confetti should keep a fallback timeout (Review already does).
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      container.innerHTML = '';
      startTimeRef.current = null;
    };
  }, [trigger, level]);

  return (
    <div
      ref={containerRef}
      className="confetti-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    />
  );
}

export default ConfettiAnimation;
